import React, { useEffect, useState, useRef, useMemo } from 'react';
import ReactToPrint from 'react-to-print';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import MotionPictureBudget from './MotionPictureBudget';
import Button from 'components/Button';
import { PrinterIcon, DownloadIcon, ArrowBack } from 'utils/icons';
import AicpBudgetData from './AicpBudget';
import { useAppContext } from 'contexts/AppContext';
import { isAicpTemplateName, userActivity } from 'utils/constants';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import { useBudgetMutation } from 'utils/api/mutations';
import { useBudgetQueries, useGlobalQuery } from 'utils/api/queries';
import {
  createTimestampedPdfFileName,
  downloadFile,
  getLocalTimezone,
  toastStyle,
} from 'utils/helper';
import {
  getPrintPaperCssVariables,
  getPrintPaperPageStyle,
  persistPrintPaperSize,
} from 'utils/printPaperSizes';
import { filterBudgetForPrint } from './budgetFilters';
import {
  getDefaultJobInformation,
  getDefaultProductionInformation,
  normalizePrintInformationSection,
} from './ProductionInfoBlocks';
import usePDFOptions from './usePDFOptions';
import PDFOptionsPanel from './PDFOptionsPanel';
import {
  fallbackPDFCatalog,
  pdfOptionDefaults,
  pdfOptionsPayload,
} from 'utils/pdfOptions';

import './styles.scss';

const rowHasOvertime = row =>
  [1, 2, 3, 4].some(slot =>
    Boolean(Number.parseFloat(row?.[`ot${slot}_quantity`]) || 0)
  );

const lineItemHasOvertime = lineItem =>
  (lineItem?.swimlane_item_detail_sheets || []).some(rowHasOvertime) ||
  (lineItem?.children || []).some(lineItemHasOvertime);

const budgetHasOvertime = budget =>
  (budget?.budget_swimlane || []).some(
    category =>
      (category?.swimlane_detail_sheets || []).some(rowHasOvertime) ||
      (category?.budget_swimlane_lineitem || []).some(lineItemHasOvertime)
  );

const PrintBudget = ({
  budgetId: propBudgetId,
  exportPdf,
  onExportCompleted,
}) => {
  const componentRef = useRef(null);
  const [uniqId, setUniqId] = useState(new Date()?.valueOf());
  const { userActivityMutation } = useAppContext();
  const { hasEditAccess, selectedBudget } = useHomeScreenContext();
  const budgetDownloadMutation = useBudgetMutation.useBudgetDownloadMutation();
  const budgetUpdateMutation = useBudgetMutation.useCreateBudgetMutation();
  const assumptionsSaveTimeoutRef = useRef(null);
  const printInformationSaveTimeoutRef = useRef(null);
  const handleDownloadRef = useRef(null);
  const lastSavedAssumptionsRef = useRef({
    title: '',
    content: '',
  });
  const lastSavedPrintInformationRef = useRef({
    job: null,
    production: null,
  });
  const [hasCheckedBudgetId, setHasCheckedBudgetId] = useState(false);

  // Extract budget ID from URL
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const urlBudgetId = queryParams.get('id');
  const budgetId = propBudgetId || urlBudgetId;

  // Fetch budget data using the ID from URL
  const { data, isLoading } = useBudgetQueries.useBudgetDataQuery({
    budgetId,
    enabled: Boolean(budgetId),
  });
  const budgetData = data?.data;
  const { data: budgetTypesData } = useBudgetQueries.useBudgetTypeQuery({
    enabled: Boolean(budgetId),
  });
  const budgetTypes = budgetTypesData?.data;

  const navigate = useNavigate();
  const [productionAssumptionsTitle, setProductionAssumptionsTitle] = useState(
    'Production Assumptions & Notes'
  );
  const [productionAssumptions, setProductionAssumptions] = useState('');
  const [jobInformationSection, setJobInformationSection] = useState(null);
  const [productionInformationSection, setProductionInformationSection] =
    useState(null);
  const [
    productionAssumptionsInitializedBudgetId,
    setProductionAssumptionsInitializedBudgetId,
  ] = useState(null);
  const [
    printInformationInitializedBudgetId,
    setPrintInformationInitializedBudgetId,
  ] = useState(null);

  const selectedTemplateId = budgetData?.template_type?.toString();
  // Use the motion picture layout when no template is assigned.
  const selectedTemplate = selectedTemplateId || '2';
  const selectedTemplateName = useMemo(
    () =>
      (budgetTypes || []).find(
        type => type?.id?.toString() === selectedTemplateId?.toString()
      )?.name || '',
    [budgetTypes, selectedTemplateId]
  );
  const useAicpPrintLayout =
    selectedTemplate === '1' || isAicpTemplateName(selectedTemplateName);
  const pdf = usePDFOptions(budgetId, selectedTemplateName);
  const pdfValues = useMemo(
    () =>
      pdf.values || pdfOptionDefaults(fallbackPDFCatalog(selectedTemplateName)),
    [pdf.values, selectedTemplateName]
  );
  const {
    include_comments: includeComments,
    include_cinebudget_branding: includeCineBudgetBranding,
    include_production_details: showProductionInfoHeaders,
    include_job_information_section: showJobInformationSection,
    include_production_information_section: showProductionInformationSection,
    include_production_assumptions: includeProductionAssumptions,
    include_used_globals: includeUsedGlobals,
    include_account_numbers: includeAccountNumbers,
    include_actuals: includeActuals,
    include_fringe_details: includeFringeDetails,
    include_zero_value_categories: includeZeroValueCategories,
    include_zero_value_line_items: includeZeroValueLineItems,
    paper_size: paperSize,
  } = pdfValues;
  const visibleDetailFields = useMemo(
    () =>
      Object.fromEntries(
        (pdf.catalog || fallbackPDFCatalog(selectedTemplateName)).groups
          .flatMap(group => group.options)
          .find(option => option.key === 'visible_detail_fields')
          .choices.map(choice => [
            choice.value,
            pdfValues.visible_detail_fields.includes(choice.value),
          ])
      ),
    [pdf.catalog, pdfValues.visible_detail_fields, selectedTemplateName]
  );
  const visibleOvertimeSlots = useMemo(
    () =>
      Object.fromEntries(
        [1, 2, 3, 4].map(slot => [
          slot,
          pdfValues.visible_overtime_slots.includes(slot),
        ])
      ),
    [pdfValues.visible_overtime_slots]
  );
  const includeOvertimeDetails = pdfValues.visible_overtime_slots.length > 0;
  const paperOptions = useMemo(
    () =>
      pdf.catalog?.groups
        .flatMap(group => group.options)
        .find(option => option.key === 'paper_size')?.choices,
    [pdf.catalog]
  );
  const productionInfoOptionsInitializedBudgetId = pdf.catalog
    ? budgetData?.id
    : null;
  const printInformationBudgetSource = selectedBudget || budgetData;
  const defaultJobInformationSection = useMemo(
    () => getDefaultJobInformation(printInformationBudgetSource),
    [printInformationBudgetSource]
  );
  const defaultProductionInformationSection = useMemo(
    () => getDefaultProductionInformation(printInformationBudgetSource),
    [printInformationBudgetSource]
  );
  const canEditPrintInformationSections = Boolean(hasEditAccess?.());
  const visibleBudgetData = useMemo(
    () =>
      filterBudgetForPrint(budgetData, {
        includeZeroValueCategories,
        includeZeroValueLineItems,
      }),
    [budgetData, includeZeroValueCategories, includeZeroValueLineItems]
  );
  const { data: usedGlobalsData } = useGlobalQuery.useGlobalsQuery({
    filters: {
      instance_type: 'budget',
      budget_id: budgetId,
      used: true,
    },
    enabled: Boolean(budgetId && includeUsedGlobals),
  });
  const usedGlobals = usedGlobalsData?.data?.data || [];
  const printPaperStyle = useMemo(
    () => getPrintPaperCssVariables(paperSize, paperOptions),
    [paperSize, paperOptions]
  );
  const printPageStyle = useMemo(
    () => getPrintPaperPageStyle(paperSize, paperOptions),
    [paperSize, paperOptions]
  );

  useEffect(() => {
    if (pdf.catalog) persistPrintPaperSize(paperSize, paperOptions);
  }, [paperSize, paperOptions, pdf.catalog]);

  useEffect(() => {
    if (
      budgetData?.id &&
      productionAssumptionsInitializedBudgetId !== budgetData.id
    ) {
      const title =
        budgetData?.production_assumptions_title ||
        'Production Assumptions & Notes';
      const content = budgetData?.production_assumptions || '';
      setProductionAssumptionsTitle(title);
      setProductionAssumptions(content);
      lastSavedAssumptionsRef.current = { title, content };
      setProductionAssumptionsInitializedBudgetId(budgetData.id);
    }
  }, [
    budgetData?.id,
    budgetData?.production_assumptions,
    budgetData?.production_assumptions_title,
    productionAssumptionsInitializedBudgetId,
  ]);

  useEffect(() => {
    if (
      budgetData?.id &&
      printInformationInitializedBudgetId !== budgetData.id
    ) {
      const jobSection = normalizePrintInformationSection(
        budgetData?.print_job_information,
        defaultJobInformationSection
      );
      const productionSection = normalizePrintInformationSection(
        budgetData?.print_production_information,
        defaultProductionInformationSection
      );

      setJobInformationSection(jobSection);
      setProductionInformationSection(productionSection);
      lastSavedPrintInformationRef.current = {
        job: jobSection,
        production: productionSection,
      };
      setPrintInformationInitializedBudgetId(budgetData.id);
    }
  }, [
    budgetData?.id,
    budgetData?.print_job_information,
    budgetData?.print_production_information,
    defaultJobInformationSection,
    defaultProductionInformationSection,
    printInformationInitializedBudgetId,
  ]);

  useEffect(() => {
    setUniqId(new Date().valueOf());
  }, [
    includeComments,
    includeCineBudgetBranding,
    showProductionInfoHeaders,
    showJobInformationSection,
    showProductionInformationSection,
    includeProductionAssumptions,
    includeUsedGlobals,
    includeAccountNumbers,
    includeActuals,
    includeZeroValueCategories,
    includeZeroValueLineItems,
    visibleDetailFields,
    visibleOvertimeSlots,
    paperSize,
    budgetData,
  ]);

  useEffect(
    () => () => {
      if (assumptionsSaveTimeoutRef.current) {
        clearTimeout(assumptionsSaveTimeoutRef.current);
      }
      if (printInformationSaveTimeoutRef.current) {
        clearTimeout(printInformationSaveTimeoutRef.current);
      }
    },
    []
  );

  // Verify budget ID availability
  useEffect(() => {
    if (!hasCheckedBudgetId && exportPdf) {
      if (!budgetId) {
        console.error('Budget ID is missing but component is trying to render');
        toast.error('Budget ID is missing. Cannot generate PDF.', toastStyle);
      }
      setHasCheckedBudgetId(true);
    }
  }, [budgetId, hasCheckedBudgetId, exportPdf]);

  const getNormalizedPrintInformationSections = nextValues => ({
    job: normalizePrintInformationSection(
      nextValues?.jobInformationSection || jobInformationSection,
      defaultJobInformationSection
    ),
    production: normalizePrintInformationSection(
      nextValues?.productionInformationSection || productionInformationSection,
      defaultProductionInformationSection
    ),
  });

  const savePrintInformationSections = async nextValues => {
    if (!budgetId || !canEditPrintInformationSections) return;

    const nextSections = getNormalizedPrintInformationSections(nextValues);
    const lastSaved = lastSavedPrintInformationRef.current;

    if (
      JSON.stringify(lastSaved.job) === JSON.stringify(nextSections.job) &&
      JSON.stringify(lastSaved.production) ===
        JSON.stringify(nextSections.production)
    ) {
      return;
    }

    await budgetUpdateMutation.mutateAsync({
      id: budgetId,
      print_job_information: nextSections.job,
      print_production_information: nextSections.production,
    });
    lastSavedPrintInformationRef.current = nextSections;
  };

  const schedulePrintInformationSave = nextValues => {
    if (!budgetId || !canEditPrintInformationSections) return;
    if (printInformationSaveTimeoutRef.current) {
      clearTimeout(printInformationSaveTimeoutRef.current);
    }
    printInformationSaveTimeoutRef.current = setTimeout(() => {
      savePrintInformationSections(nextValues).catch(error => {
        console.error('Print information autosave failed:', error);
        toast.error('Could not save print information.', toastStyle);
      });
    }, 800);
  };

  const flushPrintInformationSave = async () => {
    if (printInformationSaveTimeoutRef.current) {
      clearTimeout(printInformationSaveTimeoutRef.current);
      printInformationSaveTimeoutRef.current = null;
    }
    await savePrintInformationSections({
      jobInformationSection,
      productionInformationSection,
    });
  };

  const handleJobInformationChange = value => {
    const nextSection = normalizePrintInformationSection(
      value,
      defaultJobInformationSection
    );
    setJobInformationSection(nextSection);
    schedulePrintInformationSave({
      jobInformationSection: nextSection,
      productionInformationSection,
    });
  };

  const handleProductionInformationChange = value => {
    const nextSection = normalizePrintInformationSection(
      value,
      defaultProductionInformationSection
    );
    setProductionInformationSection(nextSection);
    schedulePrintInformationSave({
      jobInformationSection,
      productionInformationSection: nextSection,
    });
  };

  const handleAfterPrint = React.useCallback(
    activity => () => {
      if (activity === userActivity.BUDGET_PRINTED) {
        userActivityMutation.mutate({
          activity_type: userActivity.BUDGET_PRINTED,
        });
      }
    },
    [userActivityMutation]
  );

  const handleOnBeforeGetContent = async () => {
    const toastId = toast.loading('Loading....');
    try {
      await flushPrintInformationSave();
      await new Promise(resolve => {
        setTimeout(resolve, 750);
      });
    } finally {
      toast.dismiss(toastId);
    }
  };

  const reactToPrintContent = React.useCallback(() => {
    return componentRef.current;
  }, []);

  const reactToPrintTrigger = React.useCallback(() => {
    return (
      <Button
        label={
          <div className="button-label">
            <PrinterIcon /> Print
          </div>
        }
        type="button"
        className="print-budget-button"
        variant="primary"
      />
    );
  }, []);

  const handleDownload = async () => {
    if (!pdf.catalog) return;
    try {
      await flushPrintInformationSave();
      if (includeProductionAssumptions) {
        await flushProductionAssumptionsSave();
      }
      const response = await budgetDownloadMutation.mutateAsync({
        budget_id: budgetId,
        ...pdfOptionsPayload(pdf.catalog, pdfValues, pdf.live),
        timezone: getLocalTimezone(),
      });

      downloadFile(response.data, {
        contentDispositionHeader: response.headers?.['content-disposition'],
        contentTypeHeader:
          response.headers?.['content-type'] || 'application/pdf',
        fileName: createTimestampedPdfFileName(budgetData?.name || 'Budget'),
      });

      userActivityMutation.mutate({
        activity_type: userActivity.BUDGET_EXPORTED,
      });

      // Notify parent component that export is complete
      if (onExportCompleted) {
        onExportCompleted(true);
      }
    } catch (error) {
      console.error('PDF generation error:', error);
      const message =
        error?.response?.data?.non_field_errors?.[0] ||
        error?.response?.data?.message?.non_field_errors?.[0] ||
        error.message;
      toast.error(
        message || 'An error occurred while downloading the budget',
        toastStyle
      );

      // Notify parent component that export failed
      if (onExportCompleted) {
        onExportCompleted(false);
      }
    }
  };

  useEffect(() => {
    handleDownloadRef.current = handleDownload;
  });

  // Initialize PDF download when in export mode. The ref keeps this effect
  // tied to readiness changes without retriggering just because the handler's
  // closure was refreshed by an option change.
  useEffect(() => {
    if (
      !exportPdf ||
      !budgetData ||
      isLoading ||
      productionInfoOptionsInitializedBudgetId !== budgetData.id ||
      productionAssumptionsInitializedBudgetId !== budgetData.id ||
      printInformationInitializedBudgetId !== budgetData.id
    ) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      handleDownloadRef.current?.();
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [
    exportPdf,
    budgetData,
    isLoading,
    productionInfoOptionsInitializedBudgetId,
    productionAssumptionsInitializedBudgetId,
    printInformationInitializedBudgetId,
  ]);

  const goBack = () => {
    navigate(-1);
  };

  const saveProductionAssumptions = async nextValues => {
    if (!budgetId) return;
    const nextTitle =
      nextValues.title?.trim?.() || 'Production Assumptions & Notes';
    const nextContent = nextValues.content || '';
    const lastSaved = lastSavedAssumptionsRef.current;

    if (lastSaved.title === nextTitle && lastSaved.content === nextContent) {
      return;
    }

    await budgetUpdateMutation.mutateAsync({
      id: budgetId,
      production_assumptions_title: nextTitle,
      production_assumptions: nextContent,
    });
    lastSavedAssumptionsRef.current = {
      title: nextTitle,
      content: nextContent,
    };
  };

  const scheduleProductionAssumptionsSave = nextValues => {
    if (!budgetId) return;
    if (assumptionsSaveTimeoutRef.current) {
      clearTimeout(assumptionsSaveTimeoutRef.current);
    }
    assumptionsSaveTimeoutRef.current = setTimeout(() => {
      saveProductionAssumptions(nextValues).catch(error => {
        console.error('Production assumptions autosave failed:', error);
        toast.error('Could not save production assumptions.', toastStyle);
      });
    }, 800);
  };

  const flushProductionAssumptionsSave = async () => {
    if (assumptionsSaveTimeoutRef.current) {
      clearTimeout(assumptionsSaveTimeoutRef.current);
      assumptionsSaveTimeoutRef.current = null;
    }
    await saveProductionAssumptions({
      title: productionAssumptionsTitle,
      content: productionAssumptions,
    });
  };

  const handleProductionAssumptionsTitleChange = value => {
    setProductionAssumptionsTitle(value);
    scheduleProductionAssumptionsSave({
      title: value,
      content: productionAssumptions,
    });
  };

  const handleProductionAssumptionsChange = value => {
    setProductionAssumptions(value);
    scheduleProductionAssumptionsSave({
      title: productionAssumptionsTitle,
      content: value,
    });
  };

  const getLabel = label => {
    switch (label) {
      case 'Multiplier Unit':
        return 'X Unit';
      case 'Quantity':
        return 'Qty';
      case 'Multiplier':
        return 'X';
      default:
        return label;
    }
  };

  // Handle case where budgetId is missing
  if (!budgetId && !isLoading) {
    return (
      <div className="p-4">
        Budget ID is missing. Please select a budget to print.
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <div
        className="print-layout-container d-flex justify-content-center align-items-center"
        style={{ minHeight: '300px' }}
      >
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading budget data...</p>
        </div>
      </div>
    );
  }

  // Show error if budget data failed to load
  if (!budgetData && !isLoading) {
    return (
      <div className="print-layout-container">
        <div className="alert alert-danger m-4">
          Failed to load budget data. Please try again later.
        </div>
      </div>
    );
  }

  if (!pdf.catalog)
    return (
      <div className="print-layout-container p-4" role="status">
        {pdf.error || 'Loading PDF options…'}
        {pdf.error && (
          <button type="button" onClick={pdf.retry}>
            Retry
          </button>
        )}
      </div>
    );

  const hasFringes = (visibleBudgetData?.budget_swimlane ?? []).some(swimlane =>
    (swimlane?.swimlane_detail_sheets ?? []).some(
      sheet =>
        sheet?.fringe &&
        Array.isArray(sheet?.fringe) &&
        sheet?.fringe.length > 0
    )
  );
  const hasOvertime = budgetHasOvertime(visibleBudgetData);

  return (
    <div className="print-layout-container">
      <div className="print-actions px-5">
        <ArrowBack onClick={goBack} className="print-actions__back-icon" />
        <Button
          label={
            <div className="button-label">
              <DownloadIcon /> Download
            </div>
          }
          type="button"
          className="download-budget-button"
          variant="primary"
          onClick={handleDownload}
        />
        <ReactToPrint
          content={reactToPrintContent}
          documentTitle={budgetData?.name || 'Budget'}
          onAfterPrint={handleAfterPrint(userActivity.BUDGET_PRINTED)}
          onBeforeGetContent={handleOnBeforeGetContent}
          pageStyle={printPageStyle}
          removeAfterPrint
          trigger={reactToPrintTrigger}
        />
      </div>
      <div className="print-content">
        <div
          className="print-preview"
          style={printPaperStyle}
          role="region"
          aria-label="Budget print preview"
        >
          {useAicpPrintLayout ? (
            <AicpBudgetData
              ref={componentRef}
              paperSize={paperSize}
              paperOptions={paperOptions}
              includeComments={includeComments}
              includeCineBudgetBranding={includeCineBudgetBranding}
              showProductionInfoHeaders={showProductionInfoHeaders}
              showJobInformationSection={showJobInformationSection}
              showProductionInformationSection={
                showProductionInformationSection
              }
              jobInformationSection={
                jobInformationSection || defaultJobInformationSection
              }
              productionInformationSection={
                productionInformationSection ||
                defaultProductionInformationSection
              }
              onJobInformationChange={handleJobInformationChange}
              onProductionInformationChange={handleProductionInformationChange}
              canEditProductionInfoSections={canEditPrintInformationSections}
              includeProductionAssumptions={includeProductionAssumptions}
              productionAssumptionsTitle={productionAssumptionsTitle}
              productionAssumptions={productionAssumptions}
              onProductionAssumptionsTitleChange={
                handleProductionAssumptionsTitleChange
              }
              onProductionAssumptionsChange={handleProductionAssumptionsChange}
              canEditProductionAssumptions={Boolean(hasEditAccess?.())}
              includeUsedGlobals={includeUsedGlobals}
              usedGlobals={usedGlobals}
              includeAccountNumbers={includeAccountNumbers}
              includeActuals={includeActuals}
              includeFringeDetails={includeFringeDetails && hasFringes}
              includeOvertimeDetails={includeOvertimeDetails && hasOvertime}
              visibleOvertimeSlots={visibleOvertimeSlots}
              visibleDetailFields={visibleDetailFields}
              budgetData={visibleBudgetData}
              isLoading={isLoading}
              key={`AICP-${uniqId}`}
              getLabel={getLabel}
            />
          ) : (
            <MotionPictureBudget
              ref={componentRef}
              paperSize={paperSize}
              paperOptions={paperOptions}
              includeComments={includeComments}
              includeCineBudgetBranding={includeCineBudgetBranding}
              showProductionInfoHeaders={showProductionInfoHeaders}
              showJobInformationSection={showJobInformationSection}
              showProductionInformationSection={
                showProductionInformationSection
              }
              jobInformationSection={
                jobInformationSection || defaultJobInformationSection
              }
              productionInformationSection={
                productionInformationSection ||
                defaultProductionInformationSection
              }
              onJobInformationChange={handleJobInformationChange}
              onProductionInformationChange={handleProductionInformationChange}
              canEditProductionInfoSections={canEditPrintInformationSections}
              includeProductionAssumptions={includeProductionAssumptions}
              productionAssumptionsTitle={productionAssumptionsTitle}
              productionAssumptions={productionAssumptions}
              onProductionAssumptionsTitleChange={
                handleProductionAssumptionsTitleChange
              }
              onProductionAssumptionsChange={handleProductionAssumptionsChange}
              canEditProductionAssumptions={Boolean(hasEditAccess?.())}
              includeUsedGlobals={includeUsedGlobals}
              usedGlobals={usedGlobals}
              includeAccountNumbers={includeAccountNumbers}
              includeActuals={includeActuals}
              includeFringeDetails={includeFringeDetails && hasFringes}
              includeOvertimeDetails={includeOvertimeDetails && hasOvertime}
              visibleOvertimeSlots={visibleOvertimeSlots}
              visibleDetailFields={visibleDetailFields}
              budgetData={visibleBudgetData}
              isLoading={isLoading}
              key={`motion-picture-${uniqId}`}
              getLabel={getLabel}
            />
          )}
        </div>
        {!pdf.live && (
          <p role="status">
            This server is using saved PDF options. New options will appear
            after the server is updated.{' '}
            <button type="button" onClick={pdf.retry}>
              Refresh options
            </button>
          </p>
        )}
        <PDFOptionsPanel
          catalog={pdf.catalog}
          values={pdfValues}
          onChange={pdf.change}
        />
      </div>
    </div>
  );
};

PrintBudget.defaultProps = {
  budgetId: null,
  exportPdf: false,
  onExportCompleted: null,
};

PrintBudget.propTypes = {
  budgetId: PropTypes.string,
  exportPdf: PropTypes.bool,
  onExportCompleted: PropTypes.func,
};

export default PrintBudget;
